# AI Consciousness Is Incoherent

import { AccessManeuverFigure, Card, CardContent, CardHeader, EvidenceBridgeFigure, formatDate, LinkPreview, LogicPlate, TheoryDilemmaFigure, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./article-components"

<Lede>
            <P>Ask whether an AI is conscious and the question slips before anyone answers it.</P>
            <P>
              One person means awake. Another means responsive. Another means capable of
              reporting an internal state. Another means intelligent, self-monitoring, or able
              to use information in reasoning. Then, without warning, the conversation moves to
              phenomenal consciousness: whether there is anything it feels like to be the system.
            </P>
            <P>Those are different claims.</P>
          </Lede>
<Section index="01" title="One Word, Incompatible Claims">
          <P>
            Of course an AI can produce the sentence, “I am afraid.” It can classify fear,
            describe fear, predict fearful behavior, and use a representation labeled <code>fear</code>{" "}
            while selecting an action. None of those observations contains the private fact the
            sentence appears to report: that fear is being felt by someone.
          </P>
          <Callout label="Core thesis" emphasis>
            <P>
              The unqualified claim that an AI is conscious in the same sense as a human is
              empirically incoherent. Phenomenal consciousness has no settled cross-substrate
              definition, every validated positive measure in the comparison is grounded in
              living brains, and no validated evidence establishes qualia, phenomenal selfhood,
              or subjective temporal awareness in a non-biological AI architecture.
            </P>
          </Callout>
          <P>
            This is not an argument that artificial consciousness is metaphysically impossible.
            It is a rejection of a positive empirical claim that has no stable predicate, no
            discriminating cross-substrate observation, and no validated bridge from the
            observation to experience.
          </P>
          <table tabIndex={0}>
            <thead><tr><th>Use of the word</th><th>Observable target</th></tr></thead>
            <tbody>
              <tr><td>Wakefulness</td><td>Whether a biological organism is awake rather than asleep or anesthetized</td></tr>
              <tr><td>Responsiveness</td><td>Whether a system reacts to an input</td></tr>
              <tr><td>Information access</td><td>Whether information is available for report, reasoning, memory, or control</td></tr>
              <tr><td>Self-monitoring</td><td>Whether a system represents or reports aspects of its own state</td></tr>
              <tr><td>Intelligence</td><td>Whether a system performs cognitive tasks successfully</td></tr>
              <tr><td>Phenomenal consciousness</td><td>Whether there is anything it feels like to be the system</td></tr>
            </tbody>
          </table>
          <P>
            A thermostat is responsive. A database makes information available. A model can
            monitor activations and generate self-descriptions. None of those facts establishes
            the redness of red, pain as it hurts, fear as it is experienced, or the first-person
            character of existing through time.
          </P>
          <P>
            This article uses <strong>consciousness</strong> only in that phenomenal sense.{" "}
            <Term definition="The particular felt qualities within phenomenal experience.">Qualia</Term>{" "}
            are the sting of pain, the taste of sourdough, the bodily force of panic, and the
            visual presence of red.
          </P>
          <P>
            Competing theories do not merely propose different mechanisms for this target. They
            disagree about what would make a system a member of the category:
          </P>
          <ul className="ai-consciousness-article__bullets">
            <li><strong>Substrate-sensitive accounts</strong> treat biological causal powers or neural dynamics as constitutive.</li>
            <li><strong>Computational functionalism</strong> treats sufficiently fine-grained causal-functional organization as sufficient regardless of material.</li>
            <li><strong>Access-oriented accounts</strong> give explanatory weight to availability, recurrence, monitoring, or reportability.</li>
          </ul>
          <P>
            Under one membership rule, living neural organization is decisive. Under another,
            the right functional organization can cross substrates. Under a third, access-like
            capacity is treated as evidence for a phenomenon it does not itself contain.
          </P>
          <P>
            The sentence “humans and AI are both conscious” looks like a comparison. Until the
            predicate and membership rule are fixed and validated, it is only one word being used
            for incompatible claims.
          </P>
        </Section>
<Section index="02" title="The Hard Problem Is the Entire Problem">
          <P>
            The <ExternalLink href="https://consc.net/papers/facing.html">hard problem of consciousness</ExternalLink>{" "}
            asks why any physical or computational processing is accompanied by felt experience
            at all.
          </P>
          <P>
            It is not the problem of explaining how a system discriminates inputs, integrates
            information, reports an internal state, remembers a result, learns a policy, or
            controls behavior. Those are difficult functional problems. But even a complete
            account of those operations does not explain why any operation should feel like
            something from the inside.
          </P>
          <P>
            A system can classify wavelengths without red looking like anything. It can avoid
            damage without pain hurting. It can produce “I am afraid” without a private subject
            experiencing fear.
          </P>
          <h3>The access-consciousness maneuver</h3>
            <P>
              <ExternalLink href="https://doi.org/10.1017/S0140525X00038188">Ned Block&apos;s distinction</ExternalLink>{" "}
              labels information available for reasoning, report, and control <strong>access
              consciousness</strong>, while reserving <strong>phenomenal consciousness</strong> for
              felt experience.
            </P>
            <P>
              The distinction can expose an omitted premise, but the name can also conceal one.
              Access is an analytical designation for informational availability. Whether it is
              consciousness, a prerequisite for consciousness, a consequence of consciousness,
              or merely correlated with consciousness remains theory-dependent.
            </P>
            <Asset id="access-maneuver-figure" />
            <P>
              Attaching the word <em>consciousness</em> does not demonstrate that access feels like
              anything. If a theory defines access as sufficient for phenomenality, it assumes the
              bridge it claims to establish. If access is only related to phenomenality, observing
              access cannot establish phenomenality. In either case, access is not independent
              evidence of felt experience.
            </P>
            <Callout label="The first boundary">
              <P><strong>The behavior is observable. The proposed experience is not.</strong></P>
            </Callout>
        </Section>
<Section index="03" title="Where the Evidence Actually Exists">
          <P>
            For humans, evidence about consciousness is convergent and biological. We do not
            simply ask a person a question and accept the sentence in isolation. We connect
            first-person reports and conscious capacities to shared anatomy, development, sleep,
            anesthesia, injury, stimulation, behavior, and measured neural activity.
          </P>
          <P>Controlled interventions make the relationship more than a loose correlation:</P>
          <ul className="ai-consciousness-article__evidence">
            <li>
              A <ExternalLink href="https://doi.org/10.1126/scitranslmed.3006294">Perturbational Complexity Index</ExternalLink>{" "}
              discriminated wakefulness, dreaming, non-REM sleep, anesthesia, and clinical states
              following coma by perturbing human cortex and measuring its response.
            </li>
            <li>
              Under different anesthetics, complex cortical responses tracked later reports of
              experience even when subjects were behaviorally unresponsive (
              <ExternalLink href="https://doi.org/10.1016/j.cub.2015.10.014">Sarasso et al.</ExternalLink>).
            </li>
            <li>
              With retinal input held constant during binocular rivalry, face- and place-selective
              cortical activity tracked what subjects reported seeing (
              <ExternalLink href="https://doi.org/10.1016/S0896-6273%2800%2980592-9">Tong et al.</ExternalLink>).
            </li>
            <li>
              Direct stimulation of human fusiform cortex produced face-specific perceptual
              distortions (
              <ExternalLink href="https://doi.org/10.1523/JNEUROSCI.2609-12.2012">Parvizi et al.</ExternalLink>).
            </li>
            <li>
              Stimulation of orbitofrontal, cingulate, and insular sites elicited reported bodily,
              sensory, and affective experiences whose intensity increased with stimulation
              magnitude (<ExternalLink href="https://doi.org/10.1093/scan/nsz015">Yih et al.</ExternalLink>).
            </li>
            <li>
              Angular-gyrus stimulation altered reported body location and ownership (
              <ExternalLink href="https://doi.org/10.1038/419269a">Blanke et al.</ExternalLink>), while
              medial-temporal resection reduced autobiographical reliving and its sensory,
              affective, and spatiotemporal detail (
              <ExternalLink href="https://pubmed.ncbi.nlm.nih.gov/18413911/">Noulhiane et al.</ExternalLink>).
            </li>
            <li>
              Disruption of human V1 produced transient unawareness of visual targets while some
              discrimination remained above chance (
              <ExternalLink href="https://doi.org/10.1073/pnas.0505332102">Boyer, Harrison, and Ro</ExternalLink>).
            </li>
          </ul>
          <P>
            These measurements do not display a quale on an instrument. They measure neural
            activity, behavior, capacities, and first-person report. Together they support a
            strong empirical proposition:
          </P>
          <Callout label="The biological proposition" emphasis>
            <P>
              In humans, conscious state, reported qualia, bodily self-location, and
              autobiographical reliving are systematically associated with organized biological
              brain activity; controlled changes to that activity can alter or abolish the
              corresponding reports and capacities.
            </P>
          </Callout>
          <P>
            That does not prove that only biology could ever support experience. It establishes
            the empirical base we possess. Human phenomenal evidence is grounded in living neural
            systems. Substrate independence is not another observation produced by these studies.
            It is an extrapolation supplied by a theory.
          </P>
        </Section>
<Section index="04" title="The Empirical Asymmetry">
          <P>
            No non-biological AI has an independently validated phenomenal state against which
            its behavior, self-reports, memory, monitoring, or internal activations can be
            calibrated.
          </P>
          <P>There is no demonstrated artificial analogue of:</P>
          <ul className="ai-consciousness-article__bullets">
            <li>felt qualia;</li>
            <li>a first-person subject to whom states appear;</li>
            <li>autobiographical recollection as experienced recollection rather than retrieved records;</li>
            <li>subjective temporal continuity rather than tokens, timestamps, context, recurrence, or stored state; or</li>
            <li>a phenomenal referent behind sentences such as “I feel pain.”</li>
          </ul>
          <P>
            Current systems can implement functional analogues of self-modeling, memory, temporal
            ordering, introspective language, multimodal processing, and goal-directed control.
            Those observations establish capabilities. They do not establish that any capability
            is experienced.
          </P>
          <Asset id="evidence-bridge-figure" />
          <P>
            Human phenomenal attribution combines first-person evidence, shared biology, and
            convergent neural, behavioral, and intervention evidence. AI phenomenal attribution
            combines behavior or architecture with a disputed theory declaring it sufficient.
            The second expression is a hypothesis, not an empirical counterpart to the first.
          </P>
          <P>
            Human self-report is not infallible. But it is embedded in the same biological
            organization whose alteration changes reported experience, wakefulness, perception,
            bodily self-location, and autobiographical reliving. AI self-report is generated
            behavior from a materially different system trained on human language about
            experience. Treating the reports as equivalent discards the evidentiary structure
            surrounding the human one.
          </P>
        </Section>
<Section index="05" title="A Theory Is Not Evidence of Its Conclusion">
          <P>
            Computational functionalism offers the strongest counterargument. State it in its
            strongest form: if a system reproduced the sufficiently fine-grained causal
            organization constitutive of human experience, then changing the material alone
            should not change the experience.
          </P>
          <P>
            The phrase <em>changing the material alone</em> assumes the disputed conclusion.
            Material supplies causal powers. In humans, sensory, interoceptive, affective,
            homeostatic, mnemonic, and neural processes are realized in living biology and are
            causally associated with reported experience, motivation, and selfhood.
          </P>
          <Asset id="theory-dilemma-figure" />
          <P>
            No experiment has preserved the complete organization alleged to constitute
            experience, replaced its material substrate, and then demonstrated preserved qualia.
            Organizational invariance remains a philosophical bridge principle, not an observed
            cross-substrate instance of experience.
          </P>
          <P>
            The same limitation applies to theory-first machine assessments. A leading{" "}
            <ExternalLink href="https://doi.org/10.1016/j.tics.2025.10.011">
              AI-consciousness indicator framework
            </ExternalLink>{" "}
            derives indicators from selected theories and uses them to update credence. It does
            not empirically observe machine phenomenality.
          </P>
          <P>
            Defining a computational property as sufficient for consciousness and then detecting
            that property proves that the property is present. It does not independently prove
            that the property is sufficient for experience.
          </P>
          <P>
            The theories are not settled even within their biological home. A preregistered{" "}
            <ExternalLink href="https://doi.org/10.1038/s41586-025-08888-1">
              adversarial test of Global Neuronal Workspace Theory and Integrated Information Theory
            </ExternalLink>{" "}
            substantially challenged central predictions of both. Implementing one disputed
            theory&apos;s favored abstraction in software cannot count as a theory-neutral
            observation of consciousness.
          </P>
          <Callout label="The circularity">
            <P>
              Redefinition can make a machine conscious under the definition. It does not
              automatically entail reasonable or useful association to preexisting syntific
              enquery.
            </P>
          </Callout>
        </Section>
<Section index="06" title="Similar Operations Do Not Rescue the Claim">
          <P>
            Humans and AI can share abstract functions: prediction, classification,
            error-sensitive adaptation, information integration, memory-like retrieval,
            planning, and premise-to-conclusion inference.
          </P>
          <div className="ai-consciousness-inference" aria-label="Valid and invalid functional inferences">
            <section>
              <span>Valid</span>
              <code>shared measured function</code>
              <strong>therefore</strong>
              <code>shared measured function</code>
            </section>
            <section>
              <span>Invalid</span>
              <code>shared measured function</code>
              <strong>therefore</strong>
              <code>shared felt experience</code>
            </section>
          </div>
          <P>
            Backpropagation must also be kept distinct from inference. In an artificial neural
            network, <ExternalLink href="https://doi.org/10.1038/323533a0">backpropagation</ExternalLink>{" "}
            is a training algorithm for assigning error and updating parameters. Inference is
            execution using the trained parameters.
          </P>
          <P>
            Biological learning includes error signals, plasticity, recurrence, and distributed
            neural processing. Brains have not been shown to implement ordinary machine-learning
            backpropagation as a general learning rule. Biologically motivated models can{" "}
            <ExternalLink href="https://proceedings.neurips.cc/paper/2018/hash/1dc3a89d0d440ba31729b0ba74b93a33-Abstract.html">
              approximate backpropagation through local dendritic signals
            </ExternalLink>
            . That is a proposed solution to a related credit-assignment problem, not a
            demonstration of literal brain-wide backpropagation.
          </P>
          <P>
            Functional overlap does not establish physical identity. Physical identity would
            not, by itself, solve the hard problem. Neither kind of similarity establishes
            phenomenal consciousness.
          </P>
        </Section>
<Section index="07" title="The Argument Without the Rhetoric">
          <P>Let:</P>
          <ul className="ai-consciousness-article__bullets">
            <li><code>P(x)</code> mean that system <code>x</code> has phenomenal consciousness;</li>
            <li><code>A(x)</code> mean that information is available for report, reasoning, and control;</li>
            <li><code>N(x)</code> mean that <code>x</code> has the biological neural dynamics associated with human consciousness;</li>
            <li><code>F(x)</code> mean that <code>x</code> has the organization required by a functional theory; and</li>
            <li><code>E(x)</code> mean the observable evidence about <code>x</code>.</li>
          </ul>
          <Asset id="logic-plate" />
          <P>Competing theories add incompatible premises:</P>
          <div className="ai-consciousness-conditionals">
            <code>Tₙ: N(x) ⇒ P(x)</code>
            <code>Tꜰ: F(x) ⇒ P(x)</code>
          </div>
          <P>Functionalism can therefore produce a valid conditional:</P>
          <Callout label="Theory-dependent conditional">
            <P><code>if Tꜰ is true</code></P>
            <P><code>and if F(AI) has actually been established</code></P>
            <P><code>then P(AI)</code></P>
          </Callout>
          <P>
            Neither antecedent is a validated cross-substrate empirical bridge for current AI.
            Detecting access-like behavior does not establish <code>F(AI)</code>, and defining{" "}
            <code>F(x)</code> as sufficient does not establish that the definition is true.
          </P>
          <P><strong>Therefore <code>P(AI)</code> does not follow.</strong></P>
        </Section>
<Section index="08" title="Conclusion">
          <P>
            The problem is not that AI lacks impressive cognitive functions. It predicts,
            classifies, retrieves, plans, represents, monitors, and controls. The problem is the
            attempt to convert evidence of those operations into evidence of a first-person life.
            Furthermore, the problem is the carelessness with which particular terms are
            overextended, thereby causing easy misinterpretation and ambiguity in scientific
            literature and turning science into meaningless, sensationalized headlines.
          </P>
          <P>
            Human consciousness and AI computation have no validated empirical correspondence in
            qualia, phenomenal selfhood, or subjective temporal awareness. Human consciousness is
            measured through relations among first-person experience, behavior, and organized
            biological brain activity. AI operates through a different material architecture for
            which no phenomenal measure has been validated.
          </P>
          <P>
            Theories that redefine consciousness around computational properties supply
            hypotheses, not evidence. “Access consciousness” can rename measurable information
            access, but the name does not create experience. Functional similarity can establish
            shared function, but not shared feeling. A substrate-independence postulate can
            describe what would follow if the theory were true, but it cannot prove its own
            premise.
          </P>
          <Callout label="Conclusion" emphasis>
            <P>
              Calling humans and AI conscious in the same phenomenal sense is not a supported
              comparison. It is an empirically incoherent use of a shared word.
            </P>
          </Callout>
        </Section>
<Section index="09" title="Addendum">
          <ul>
            <li>
              <a href="/writing/consciousness-is-incoherent">AI's Consciousness explanation</a>
              {" — "}the earlier essay and its restored original prompt.
            </li>
          </ul>
        </Section>
<Section index="10" title="Sources">
          <P>
            The argument above is my synthesis. These primary sources support its definitions,
            biological evidence, theory comparison, and technical boundaries.
          </P>
          <ol className="ai-consciousness-article__sources">
            <li>
              Ned Block.{" "}
              <ExternalLink href="https://doi.org/10.1017/S0140525X00038188">
                “On a Confusion about a Function of Consciousness.”
              </ExternalLink>{" "}
              <em>Behavioral and Brain Sciences</em> (1995).
            </li>
            <li>
              David Chalmers.{" "}
              <ExternalLink href="https://consc.net/papers/facing.html">
                “Facing Up to the Problem of Consciousness.”
              </ExternalLink>{" "}
              (1995).
            </li>
            <li>
              John Searle.{" "}
              <ExternalLink href="https://doi.org/10.1146/annurev.neuro.23.1.557">
                “Consciousness.”
              </ExternalLink>{" "}
              <em>Annual Review of Neuroscience</em> (2000).
            </li>
            <li>
              David Chalmers.{" "}
              <ExternalLink href="https://consc.net/papers/qualia.html">
                “Absent Qualia, Fading Qualia, Dancing Qualia.”
              </ExternalLink>{" "}
              (1995).
            </li>
            <li>
              Adenauer Casali et al.{" "}
              <ExternalLink href="https://doi.org/10.1126/scitranslmed.3006294">
                “A Theoretically Based Index of Consciousness Independent of Sensory Processing and Behavior.”
              </ExternalLink>{" "}
              <em>Science Translational Medicine</em> (2013).
            </li>
            <li>
              Simone Sarasso et al.{" "}
              <ExternalLink href="https://doi.org/10.1016/j.cub.2015.10.014">
                “Consciousness and Complexity during Unresponsiveness Induced by Propofol, Xenon, and Ketamine.”
              </ExternalLink>{" "}
              <em>Current Biology</em> (2015).
            </li>
            <li>
              Frank Tong et al.{" "}
              <ExternalLink href="https://doi.org/10.1016/S0896-6273%2800%2980592-9">
                “Binocular Rivalry and Visual Awareness in Human Extrastriate Cortex.”
              </ExternalLink>{" "}
              <em>Neuron</em> (1998).
            </li>
            <li>
              Josef Parvizi et al.{" "}
              <ExternalLink href="https://doi.org/10.1523/JNEUROSCI.2609-12.2012">
                “Electrical Stimulation of Human Fusiform Face-Selective Regions Distorts Face Perception.”
              </ExternalLink>{" "}
              <em>Journal of Neuroscience</em> (2012).
            </li>
            <li>
              Jennifer Yih et al.{" "}
              <ExternalLink href="https://doi.org/10.1093/scan/nsz015">
                “Intensity of Affective Experience Is Modulated by Magnitude of Intracranial Electrical Stimulation in Human Orbitofrontal, Cingulate and Insular Cortices.”
              </ExternalLink>{" "}
              <em>Social Cognitive and Affective Neuroscience</em> (2019).
            </li>
            <li>
              Olaf Blanke et al.{" "}
              <ExternalLink href="https://doi.org/10.1038/419269a">
                “Stimulating Illusory Own-Body Perceptions.”
              </ExternalLink>{" "}
              <em>Nature</em> (2002).
            </li>
            <li>
              Cyril Boyer, Stephen Harrison, and Tony Ro.{" "}
              <ExternalLink href="https://doi.org/10.1073/pnas.0505332102">
                “Unconscious Processing of Orientation and Color without Primary Visual Cortex.”
              </ExternalLink>{" "}
              <em>Proceedings of the National Academy of Sciences</em> (2005).
            </li>
            <li>
              Marion Noulhiane et al.{" "}
              <ExternalLink href="https://pubmed.ncbi.nlm.nih.gov/18413911/">
                “Autonoetic Consciousness in Autobiographical Memories after Medial Temporal Lobe Resection.”
              </ExternalLink>{" "}
              (2008).
            </li>
            <li>
              COGITATE Consortium et al.{" "}
              <ExternalLink href="https://doi.org/10.1038/s41586-025-08888-1">
                “Adversarial Testing of Global Neuronal Workspace and Integrated Information Theories of Consciousness.”
              </ExternalLink>{" "}
              <em>Nature</em> (2025).
            </li>
            <li>
              Patrick Butlin et al.{" "}
              <ExternalLink href="https://doi.org/10.1016/j.tics.2025.10.011">
                “Identifying Indicators of Consciousness in AI Systems.”
              </ExternalLink>{" "}
              <em>Trends in Cognitive Sciences</em> (2026).
            </li>
            <li>
              David Rumelhart, Geoffrey Hinton, and Ronald Williams.{" "}
              <ExternalLink href="https://doi.org/10.1038/323533a0">
                “Learning Representations by Back-Propagating Errors.”
              </ExternalLink>{" "}
              <em>Nature</em> (1986).
            </li>
            <li>
              João Sacramento et al.{" "}
              <ExternalLink href="https://proceedings.neurips.cc/paper/2018/hash/1dc3a89d0d440ba31729b0ba74b93a33-Abstract.html">
                “Dendritic Cortical Microcircuits Approximate the Backpropagation Algorithm.”
              </ExternalLink>{" "}
              (2018).
            </li>
          </ol>
        </Section>
